import React, { useState, useEffect } from 'react';
import { Music, Globe, Zap, BarChart3, Headphones, Share2, Shield, Trophy, Upload, PlayCircle, FileAudio, Settings, Music2, Disc3, Sparkles, Layers, Star, Users, Heart } from 'lucide-react';
import { Link } from 'react-router-dom';
import ContactForm from '../components/ContactForm';

const Home = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [isContactFormOpen, setIsContactFormOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const scrolled = window.scrollY > 100;
      setIsVisible(scrolled);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="relative">
      {/* Scroll to Top Button */}
      <button
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        className={`fixed bottom-8 right-8 bg-purple-500 p-3 rounded-full shadow-lg shadow-purple-500/30 z-50 transition-all duration-300 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-16'
        }`}
      >
        <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
        </svg>
      </button>

      {/* Hero Section */}
      <section 
        className="relative min-h-[800px] flex items-center"
        style={{
          backgroundImage: 'url("https://images.unsplash.com/photo-1478737270239-2f02b77fc618?ixlib=rb-1.2.1&auto=format&fit=crop&w=3200&q=80")',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-black via-black/90 to-purple-900/50"></div>
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNTAwIiBoZWlnaHQ9IjUwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZGVmcz48cGF0dGVybiBpZD0iZ3JpZCIgd2lkdGg9IjUwIiBoZWlnaHQ9IjUwIiBwYXR0ZXJuVW5pdHM9InVzZXJTcGFjZU9uVXNlIj48cGF0aCBkPSJNIDUwIDAgTCAwIDAgMCA1MCIgZmlsbD0ibm9uZSIgc3Ryb2tlPSJyZ2JhKDEyNCw1OCwyMzcsMC4xKSIgc3Ryb2tlLXdpZHRoPSIxIi8+PC9wYXR0ZXJuPjwvZGVmcz48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSJ1cmwoI2dyaWQpIi8+PC9zdmc+')] opacity-10"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-white py-20">
          <div className="max-w-3xl">
            <h1 className="text-6xl md:text-7xl font-bold mb-6 leading-tight">
              Your Music Deserves
              <span className="block bg-clip-text text-transparent bg-gradient-to-r from-purple-400 to-purple-600">
                To Be Heard
              </span>
            </h1>
            <p className="text-xl mb-8 text-gray-300 leading-relaxed">
              Professional music distribution to all major streaming platforms. Simple pricing, powerful analytics, and industry-leading support.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link 
                to="/pricing"
                className="bg-gradient-to-r from-purple-500 to-purple-700 px-8 py-4 rounded-md text-lg font-semibold hover:from-purple-600 hover:to-purple-800 transition-all transform hover:scale-105 shadow-lg shadow-purple-500/30 inline-flex items-center justify-center gap-2 group"
              >
                <Music2 className="h-5 w-5 group-hover:rotate-180 transition-transform duration-300" />
                Start Distribution
              </Link>
              <Link 
                to="/pricing" 
                className="border-2 border-purple-500 px-8 py-4 rounded-md text-lg font-semibold hover:bg-purple-500/20 transition-all text-center inline-flex items-center justify-center gap-2 group relative overflow-hidden"
              >
                <span className="absolute inset-0 bg-gradient-to-r from-purple-500/0 via-purple-500/20 to-purple-500/0 animate-shimmer"></span>
                <Layers className="h-5 w-5 group-hover:scale-110 transition-transform" />
                View Pricing
              </Link>
            </div>
            <div className="mt-12 grid grid-cols-2 sm:grid-cols-4 gap-8">
              <StatsCard number="150+" label="Platforms" icon={<Globe className="h-5 w-5" />} />
              <StatsCard number="24h" label="Support" icon={<Headphones className="h-5 w-5" />} />
              <StatsCard number="100%" label="Rights Kept" icon={<Shield className="h-5 w-5" />} />
              <StatsCard number="100%" label="Revenue Share" icon={<Trophy className="h-5 w-5" />} />
            </div>
          </div>
        </div>
        
        {/* Animated Wave */}
        <div className="absolute bottom-0 left-0 right-0">
          <svg className="waves" xmlns="http://www.w3.org/2000/svg" viewBox="0 24 150 28" preserveAspectRatio="none">
            <defs>
              <path id="wave" d="M-160 44c30 0 58-18 88-18s 58 18 88 18 58-18 88-18 58 18 88 18 v44h-352z" />
            </defs>
            <g className="parallax">
              <use href="#wave" x="48" y="0" fill="rgba(124,58,237,0.2)" />
              <use href="#wave" x="48" y="3" fill="rgba(124,58,237,0.1)" />
              <use href="#wave" x="48" y="5" fill="rgba(124,58,237,0.05)" />
              <use href="#wave" x="48" y="7" fill="rgba(0,0,0,1)" />
            </g>
          </svg>
        </div>
      </section>

      {/* Distribution Process */}
      <section className="bg-black text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-purple-500/20 rounded-full border border-purple-500/30 mb-4">
              <Settings className="h-4 w-4 text-purple-400 animate-spin-slow" />
              <span className="text-sm font-medium">Simple 4-Step Process</span>
            </div>
            <h2 className="text-4xl font-bold mb-4">How It Works</h2>
            <p className="text-xl text-gray-400">Simple steps to get your music worldwide</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <ProcessStep 
              icon={<Upload className="h-8 w-8 text-purple-400" />}
              title="Upload"
              description="Upload your tracks in high-quality WAV format"
              number="1"
            />
            <ProcessStep 
              icon={<FileAudio className="h-8 w-8 text-purple-400" />}
              title="Prepare"
              description="Add artwork and metadata for all platforms"
              number="2"
            />
            <ProcessStep 
              icon={<Settings className="h-8 w-8 text-purple-400" />}
              title="Review"
              description="Our team checks everything for quality"
              number="3"
            />
            <ProcessStep 
              icon={<PlayCircle className="h-8 w-8 text-purple-400" />}
              title="Release"
              description="Your music goes live on all platforms"
              number="4"
            />
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-gradient-to-b from-gray-900 to-black text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAxMCAwIEwgMCAwIDAgMTAiIGZpbGw9Im5vbmUiIHN0cm9rZT0icmdiYSgxMjQsNTgsMjM3LDAuMSkiIHN0cm9rZS13aWR0aD0iMSIvPjwvcGF0dGVybj48L2RlZnM+PHJlY3Qgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgZmlsbD0idXJsKCNncmlkKSIvPjwvc3ZnPg==')] opacity-10"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-purple-500/20 rounded-full border border-purple-500/30 mb-4">
              <Heart className="h-4 w-4 text-purple-400" />
              <span className="text-sm font-medium">Why Choose Us</span>
            </div>
            <h2 className="text-4xl font-bold mb-4">Why Artists Choose Distrotopia</h2>
            <p className="text-xl text-gray-400">Professional distribution tools designed for independent artists</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <FeatureCard 
              icon={<Globe className="h-8 w-8 text-purple-400" />}
              title="Global Reach"
              description="Get your music on Spotify, Apple Music, Amazon Music, and 150+ platforms worldwide"
            />
            <FeatureCard 
              icon={<Zap className="h-8 w-8 text-purple-400" />}
              title="Fast Release"
              description="Express delivery within 24-48 hours to major platforms"
            />
            <FeatureCard 
              icon={<BarChart3 className="h-8 w-8 text-purple-400" />}
              title="Pro Analytics"
              description="Real-time streaming data and revenue tracking across all platforms"
            />
            <FeatureCard 
              icon={<Music className="h-8 w-8 text-purple-400" />}
              title="Full Control"
              description="Keep 100% of your rights and up to 85% of streaming revenue"
            />
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-20 relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1511379938547-c1f69419868d?ixlib=rb-1.2.1&auto=format&fit=crop&w=2000&q=80')] bg-cover bg-center opacity-20"></div>
          <div className="absolute inset-0 bg-gradient-to-br from-purple-900/50 via-black/80 to-black"></div>
        </div>
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-black/40 p-12 rounded-3xl backdrop-blur-xl border border-purple-500/20 shadow-2xl shadow-purple-500/10">
            <div className="absolute -top-6 left-1/2 transform -translate-x-1/2">
              <div className="bg-gradient-to-r from-purple-500 to-purple-700 p-3 rounded-full shadow-lg shadow-purple-500/30">
                <Music2 className="h-8 w-8 text-white animate-pulse" />
              </div>
            </div>
            <div className="text-center">
              <h2 className="text-4xl font-bold mb-6">
                Ready to Share Your
                <span className="block bg-clip-text text-transparent bg-gradient-to-r from-purple-400 to-purple-600 mt-2">
                  Music with the World?
                </span>
              </h2>
              <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
                Start your journey today with our transparent pricing plans.
              </p>
              <div className="flex flex-col sm:flex-row gap-6 justify-center">
                <button 
                  onClick={() => setIsContactFormOpen(true)}
                  className="bg-gradient-to-r from-purple-500 to-purple-700 px-8 py-4 rounded-xl text-lg font-semibold hover:from-purple-600 hover:to-purple-800 transition-all transform hover:scale-105 shadow-lg shadow-purple-500/30 text-white inline-flex items-center justify-center gap-2 group"
                >
                  <Music2 className="h-5 w-5 group-hover:animate-spin" />
                  Start Distribution
                </button>
                <Link 
                  to="/pricing" 
                  className="bg-white/5 backdrop-blur-lg border-2 border-purple-500 px-8 py-4 rounded-xl text-lg font-semibold hover:bg-purple-500/20 transition-all text-white inline-flex items-center justify-center gap-2 group"
                >
                  <Sparkles className="h-5 w-5 group-hover:animate-pulse" />
                  Compare Features
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <ContactForm isOpen={isContactFormOpen} onClose={() => setIsContactFormOpen(false)} />
    </div>
  );
};

const StatsCard = ({ number, label, icon }: { number: string, label: string, icon: React.ReactNode }) => {
  return (
    <div className="text-center group">
      <div className="inline-flex items-center justify-center mb-2">
        <div className="p-2 rounded-lg bg-purple-500/20 text-purple-400 group-hover:scale-110 transition-transform">
          {icon}
        </div>
      </div>
      <div className="text-3xl font-bold text-purple-400 group-hover:text-purple-300 transition-colors">{number}</div>
      <div className="text-sm text-gray-400">{label}</div>
    </div>
  );
};

const FeatureCard = ({ icon, title, description }: { icon: React.ReactNode, title: string, description: string }) => {
  return (
    <div className="bg-gray-900/50 p-8 rounded-lg border border-purple-500/20 hover:border-purple-500/40 transition-all transform hover:scale-105 group">
      <div className="flex justify-center mb-4">
        <div className="p-3 rounded-lg bg-purple-500/20 group-hover:bg-purple-500/30 transition-colors">
          {icon}
        </div>
      </div>
      <h3 className="text-xl font-semibold mb-2 text-center group-hover:text-purple-400 transition-colors">{title}</h3>
      <p className="text-gray-400 text-center">{description}</p>
    </div>
  );
};

const ProcessStep = ({ icon, title, description, number }: { icon: React.ReactNode, title: string, description: string, number: string }) => {
  return (
    <div className="relative flex flex-col items-center text-center group">
      <div className="absolute -top-4 -left-4 text-4xl font-bold text-purple-500/20">{number}</div>
      <div className="bg-purple-900/30 p-4 rounded-lg mb-4 group-hover:bg-purple-900/50 transition-all transform group-hover:scale-110">
        {icon}
      </div>
      <h3 className="text-xl font-semibold mb-2 group-hover:text-purple-400 transition-colors">{title}</h3>
      <p className="text-gray-400">{description}</p>
      {number !== "4" && (
        <div className="hidden md:block absolute top-1/2 left-full w-full h-0.5 bg-gradient-to-r from-purple-500/50 to-transparent transform -translate-y-1/2"></div>
      )}
    </div>
  );
};

export default Home;